// ./src/components/features/DashboardLayout/DashboardLayout.tsx
import React from "react";
import {
  AppShell,
  Burger,
  Group,
  Text,
  NavLink,
  ScrollArea,
  Avatar,
  Menu,
  Alert,
  Box,
  Stack,
  Divider,
  Button,
  UnstyledButton,
  ActionIcon,
  CloseButton,
  Tooltip,
  Overlay,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { Logo } from "../../generic/Logo";
import { AppIcon } from "../../generic/AppIcon";
import { useApp } from "../../../contexts/AppContext";
import { SiteMapItem } from "../../../config/siteMap";
import { canUserAccess } from "@/utils";
import { NoAccessMessage } from "@/components/generic";
import classes from "./DashboardLayout.module.css";
import { USER_ROLES } from "@/config/userRoles";

// ============================================
// Types
// ============================================

type FooterMessageType = "info" | "success" | "warning" | "error";

interface DashboardLayoutProps {
  children: React.ReactNode;
  page: {
    name?: string;
    subtitle?: string;
    icon?: string;
    roles?: string[];
  };
  footerMessage?: string;
  footerMessageType?: FooterMessageType;
  showFooterMessage?: boolean;
}

// ============================================
// Constants
// ============================================

const FOOTER_ICON_MAP: Record<FooterMessageType, string> = {
  success: "Check",
  warning: "AlertTriangle",
  error: "X",
  info: "InfoCircle",
};

const FOOTER_COLOR_MAP: Record<FooterMessageType, string> = {
  success: "success",
  warning: "warning",
  error: "error",
  info: "brand",
};

// ============================================
// Component
// ============================================

export const DashboardLayout: React.FC<DashboardLayoutProps> = (
  props: DashboardLayoutProps
) => {
  const {
    children,
    page,
    footerMessage,
    footerMessageType = "info",
    showFooterMessage = false,
  } = props;
  const [sidebarOpened, { toggle: toggleSidebar, close: closeSidebar }] =
    useDisclosure();
  const [desktopCollapsed, { toggle: toggleDesktopSidebar }] =
    useDisclosure(false);
  const [userMenuOpened, { toggle: toggleUserMenu, close: closeUserMenu }] =
    useDisclosure();

  const isMobile = useMediaQuery("(max-width: 61.99em)");

  const navigate = useNavigate();
  const location = useLocation();

  const {
    user,
    userRoles,
    getVisibleRoutes,
    signOut,
    subscriptionStatus,
    trialEndsAt,
  } = useApp();

  const navigationItems = getVisibleRoutes();
  const { name, subtitle, icon } = page;
  const hasAccess = canUserAccess(page.roles, userRoles);

  // ============================================
  // Handlers
  // ============================================

  const handleNavClick = (path: string) => {
    navigate(path);
    closeSidebar();
  };

  const handleSignOut = () => {
    signOut();
    navigate("/account/signin");
    closeUserMenu();
  };

  const handleMenuAction = (path: string) => {
    navigate(path);
    closeUserMenu();
  };

  // ============================================
  // Subscription Footer Logic
  // ============================================

  const getSubscriptionFooterConfig = () => {
    if (location.pathname === "/account/subscription") return null;

    if (subscriptionStatus === "trial" && trialEndsAt) {
      const daysRemaining = dayjs(trialEndsAt).diff(dayjs(), "day");
      if (daysRemaining > 3) return null;

      return {
        message: `Your trial ends in ${daysRemaining} ${
          daysRemaining === 1 ? "day" : "days"
        }. Upgrade now to continue using FormSubmits.`,
        color: "warning",
        icon: "AlertTriangle",
        buttonText: "Upgrade Now",
      };
    }

    if (subscriptionStatus === "expired") {
      return {
        message:
          "Your trial has ended. Subscribe now to continue using FormSubmits.",
        color: "error",
        icon: "AlertCircle",
        buttonText: "Subscribe Now",
      };
    }

    if (subscriptionStatus === "cancelled") {
      return {
        message:
          "Your subscription has been cancelled. Reactivate to continue using FormSubmits.",
        color: "warning",
        icon: "AlertTriangle",
        buttonText: "Reactivate",
      };
    }

    return null;
  };

  const subscriptionFooter = getSubscriptionFooterConfig();
  const showSubscriptionFooter = Boolean(subscriptionFooter);
  const hasFooter = showFooterMessage || showSubscriptionFooter;

  // ============================================
  // Navigation Helpers
  // ============================================

  const isItemActive = (item: SiteMapItem): boolean => {
    if (item.url && location.pathname === item.url) return true;
    if (item.items?.length) return item.items.some(isItemActive);
    return false;
  };

  const renderNavItem = (item: SiteMapItem, index: number) => {
    const hasChildren = Array.isArray(item.items) && item.items.length > 0;

    if (hasChildren) {
      const isActive = isItemActive(item);
      return (
        <NavLink
          key={`parent-${item.name}-${index}`}
          active={isActive}
          label={item.name}
          leftSection={
            <AppIcon iconName={item.icon || "Dashboard"} size={18} />
          }
          childrenOffset={28}
          defaultOpened={isActive}
          className={classes.navLink}
        >
          {item.items!.map((child, childIndex) =>
            renderNavItem(child, childIndex)
          )}
        </NavLink>
      );
    }

    if (item.url) {
      return (
        <NavLink
          key={`leaf-${item.url}-${index}`}
          active={location.pathname === item.url}
          label={item.name}
          leftSection={
            <AppIcon iconName={item.icon || "Dashboard"} size={18} />
          }
          onClick={() => handleNavClick(item.url!)}
          className={classes.navLink}
        />
      );
    }

    return null;
  };

  // ============================================
  // User Initials
  // ============================================

  const userInitials =
    user?.username
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "?";

  const isOwner = user?.userRole?.id === USER_ROLES.OWNER;

  // ============================================
  // Sidebar Content
  // ============================================

  const sidebarContent = (
    <Stack h="100%" gap={0} className={classes.sidebar}>
      {/* Mobile Header - Logo + Close Button */}
      <Box className={classes.mobileNavHeader}>
        <Group justify="space-between" align="center" h="100%" px="md">
          <Logo size="small" withName />
          <CloseButton
            size="lg"
            onClick={closeSidebar}
            aria-label="Close navigation"
          />
        </Group>
      </Box>

      {/* Desktop Logo Section */}
      <Box className={classes.logoSection}>
        <Group justify="space-between" align="center" w="100%">
          <Logo size="small" withName />
          <Tooltip label="Collapse sidebar" position="right" withArrow>
            <ActionIcon
              variant="subtle"
              color="neutral"
              onClick={toggleDesktopSidebar}
              className={classes.collapseButton}
              aria-label="Collapse sidebar"
            >
              <AppIcon iconName="ChevronsLeft" size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Box>

      <Divider
        color="var(--mantine-color-neutral-2)"
        className={classes.logoDivider}
      />

      {/* Navigation */}
      <ScrollArea flex={1} className={classes.navSection} scrollbarSize={6}>
        <Stack gap={2}>
          {navigationItems.map((item, index) => renderNavItem(item, index))}
        </Stack>
      </ScrollArea>

      <Divider
        color="var(--mantine-color-neutral-2)"
        className={classes.userDivider}
      />

      {/* User Section */}
      <Box className={classes.userSection}>
        {user && (
          <Menu
            shadow="lg"
            width={240}
            position="top-start"
            opened={userMenuOpened}
            onChange={toggleUserMenu}
            withinPortal
            transitionProps={{ transition: "pop", duration: 150 }}
          >
            <Menu.Target>
              <UnstyledButton
                className={classes.userButton}
                data-active={userMenuOpened || undefined}
              >
                <Group gap="sm" wrap="nowrap">
                  <Avatar size={36} color="brand" radius="md">
                    {userInitials}
                  </Avatar>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      size="sm"
                      fw={600}
                      truncate
                      className={classes.userName}
                    >
                      {user.username}
                    </Text>
                    <Text size="xs" truncate className={classes.userEmail}>
                      {user.email}
                    </Text>
                  </Box>
                  <AppIcon
                    iconName="ChevronUp"
                    size={14}
                    className={classes.chevron}
                    data-rotated={!userMenuOpened || undefined}
                  />
                </Group>
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown className={classes.menuDropdown}>
              <Menu.Label>Account</Menu.Label>
              <Menu.Item
                leftSection={<AppIcon iconName="User" size={16} />}
                onClick={() => handleMenuAction("/account/profile")}
              >
                Profile
              </Menu.Item>
              {isOwner && (
                <>
                  <Menu.Item
                    leftSection={<AppIcon iconName="Settings" size={16} />}
                    onClick={() => handleMenuAction("/account/settings")}
                  >
                    Settings
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<AppIcon iconName="CreditCard" size={16} />}
                    onClick={() => handleMenuAction("/account/subscription")}
                  >
                    Subscription
                  </Menu.Item>
                </>
              )}

              <Menu.Divider />
              <Menu.Item
                leftSection={<AppIcon iconName="Logout" size={16} />}
                color="error"
                onClick={handleSignOut}
              >
                Sign Out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}

        {/* Version */}
        <Text size="xs" ta="center" mt="sm" className={classes.version}>
          v{import.meta.env.VITE_APP_VERSION}
        </Text>
      </Box>
    </Stack>
  );

  // Only render sidebar content when not collapsed on desktop
  const shouldShowSidebarContent = isMobile || !desktopCollapsed;

  // ============================================
  // Render
  // ============================================

  return (
    <AppShell
      navbar={{
        width: desktopCollapsed ? 0 : 260,
        breakpoint: "md",
        collapsed: {
          mobile: !sidebarOpened,
          desktop: desktopCollapsed,
        },
      }}
      footer={hasFooter ? { height: { base: 90, sm: 60 } } : undefined}
      padding={0}
      className={classes.shell}
    >
      {/* Mobile Header */}
      <Box className={classes.mobileHeader} hiddenFrom="md">
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={sidebarOpened} onClick={toggleSidebar} size="sm" />
            <Logo size="small" />
          </Group>
        </Group>
      </Box>

      {/* Mobile Overlay - click to close sidebar */}
      {isMobile && sidebarOpened && (
        <Overlay
          onClick={closeSidebar}
          backgroundOpacity={0.3}
          zIndex={150}
          className={classes.overlay}
        />
      )}

      {/* Sidebar */}
      <AppShell.Navbar className={classes.navbar}>
        {shouldShowSidebarContent && sidebarContent}
      </AppShell.Navbar>

      {/* Footer */}
      {hasFooter && (
        <AppShell.Footer className={classes.footer}>
          {showSubscriptionFooter && subscriptionFooter && (
            <Alert
              icon={<AppIcon iconName={subscriptionFooter.icon} size={16} />}
              color={subscriptionFooter.color}
              variant="light"
              className={classes.footerAlert}
            >
              <Group justify="space-between" w="100%" gap="sm" wrap="nowrap">
                <Text size="sm" style={{ flex: 1 }}>
                  {subscriptionFooter.message}
                </Text>
                <Button
                  size="xs"
                  color={subscriptionFooter.color}
                  onClick={() => navigate("/account/subscription")}
                >
                  {subscriptionFooter.buttonText}
                </Button>
              </Group>
            </Alert>
          )}

          {showFooterMessage && footerMessage && !showSubscriptionFooter && (
            <Alert
              icon={
                <AppIcon
                  iconName={FOOTER_ICON_MAP[footerMessageType]}
                  size={16}
                />
              }
              color={FOOTER_COLOR_MAP[footerMessageType]}
              variant="light"
              className={classes.footerAlert}
            >
              {footerMessage}
            </Alert>
          )}
        </AppShell.Footer>
      )}

      {/* Main Content */}
      <AppShell.Main className={classes.main}>
        {/* Page Header */}
        <Box className={classes.pageHeader}>
          <Group justify="space-between" align="flex-start">
            <Group gap="md" align="center">
              {/* Desktop Expand Button - inside page header when collapsed */}
              {desktopCollapsed && !isMobile && (
                <Tooltip label="Expand sidebar" position="right" withArrow>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    onClick={toggleDesktopSidebar}
                    className={classes.expandButton}
                    aria-label="Expand sidebar"
                  >
                    <AppIcon iconName="Menu" size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
              {/* Page Icon */}
              {icon && (
                <Box className={classes.pageIcon}>
                  <AppIcon iconName={icon} size={28} />
                </Box>
              )}
              <Box>
                <Text component="h1" className={classes.pageTitle}>
                  {name}
                </Text>
                {subtitle && (
                  <Text className={classes.pageSubtitle}>{subtitle}</Text>
                )}
              </Box>
            </Group>
          </Group>
        </Box>

        {/* Page Content */}
        <Box className={classes.pageContent}>
          {!hasAccess ? <NoAccessMessage /> : children}
        </Box>
      </AppShell.Main>
    </AppShell>
  );
};
